/**
 * Resolve page/limit for ERP list request bodies.
 * Accepts flat `page`/`limit` or legacy `pagination: { page, limit }`.
 */
export function withListPagination(body, { page, limit, pagination } = {}) {
  const resolvedPage = page ?? pagination?.page;
  const resolvedLimit = limit ?? pagination?.limit;
  const out = { ...body };
  if (resolvedPage != null && resolvedPage !== "") {
    out.page = resolvedPage;
  }
  if (resolvedLimit != null && resolvedLimit !== "") {
    out.limit = resolvedLimit;
  }
  return out;
}

/**
 * Read pagination metadata from ERP list responses.
 * Supports `data.pagination`, top-level `pagination`, or flat fields on `data`.
 */
export function parseApiPagination(response, fallbackLength = 0) {
  const data = response?.data;
  const pag =
    (data && typeof data.pagination === "object" && data.pagination) ||
    (typeof response?.pagination === "object" && response.pagination) ||
    {};

  const page = Number(pag.page ?? data?.page) || 1;
  const limit = Number(pag.limit ?? data?.limit) || 10;
  const totalRaw =
    pag.total ??
    pag.total_count ??
    data?.total ??
    data?.total_count ??
    response?.meta?.total ??
    response?.total_count;
  const totalKnown =
    typeof totalRaw === "number" && !Number.isNaN(totalRaw);
  const hasMore = Boolean(
    pag.has_more ?? pag.hasMore ?? data?.has_more ?? data?.hasMore,
  );

  let total;
  if (totalKnown) {
    total = totalRaw;
  } else if (hasMore) {
    // API signals more pages but no total — use a floor so "Next" stays enabled.
    total = page * limit + 1;
  } else {
    total = (page - 1) * limit + fallbackLength;
  }

  const total_pages = totalKnown
    ? Number(pag.total_pages ?? data?.total_pages) ||
      Math.max(1, Math.ceil(total / limit) || 1)
    : hasMore
      ? page + 1
      : Math.max(1, Math.ceil(total / limit) || 1);

  return { page, limit, total, total_pages, hasMore, totalKnown };
}

/**
 * Read list rows from paginated ERP list responses.
 * Prefers `data.items`, then legacy array keys on `data`.
 */
export function extractListRowsFromResponse(res, legacyKeys = []) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== "object") return [];
  if (Array.isArray(d.items)) return d.items;
  for (const key of legacyKeys) {
    if (Array.isArray(d[key])) return d[key];
  }
  return [];
}
